import type { ParamsType } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';
import type { MyTableProps } from '..';
import { useRefs } from '@/scripts/hooks';
import type { FilterValue, SortOrder } from 'antd/es/table/interface';

export interface ControlModel {
  value: any;
  onChange: (value: any) => void;
}

export type UseSearchResult = ReturnType<typeof useSearch>;

export interface UseSearchProps {
  initValues?: MyRecord;
}

export function useSearch({ initValues = {} }: UseSearchProps = {}) {
  const [values, setValues] = useState<MyRecord>(initValues);

  const refs = useRefs({ initValues });

  const mergeValues = useCallback((values: MyRecord) => {
    setValues((prev) => ({ ...prev, ...values }));
  }, []);

  const setValue = useCallback((key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setValues(refs.current.initValues);
  }, [refs]);

  const controlModels = useMemo(
    () =>
      new Proxy<MyRecord<ControlModel>>(
        {},
        {
          get(_, key: string): ControlModel {
            return {
              value: values[key],
              onChange(value) {
                setValue(key, value);
              },
            };
          },
          set(_, key: string, value) {
            setValue(key, value);
            return true;
          },
        }
      ),
    [values, setValue]
  );
  return { controlModels, values, setValue, setValues, mergeValues, reset };
}

export interface UsePaginationProps {
  initValues?: Required<Pick<PaginationProps, 'current' | 'pageSize'>>;
  initStates?: Required<Pick<PaginationProps, 'total'>>;
}

export function usePagination({
  initValues = { current: 1, pageSize: 20 },
  initStates = { total: 0 },
}: UsePaginationProps = {}) {
  const [values, setValues] = useState(initValues);
  const [states, setStates] = useState(initStates);
  const refs = useRefs({ initValues });

  const handlers = useMemo<PaginationProps>(
    () => ({
      onChange: (current, pageSize) => {
        setValues({
          current,
          pageSize,
        });
      },
    }),
    []
  );

  const props = useMemo<PaginationProps>(
    () => ({
      ...values,
      ...states,
      ...handlers,
    }),
    [values, states, handlers]
  );

  const setState = useCallback((key: string, value: any) => {
    setStates((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setValues((prev) => ({
      ...prev,
      current: refs.current.initValues.current,
    }));
  }, [refs]);

  return { values, setValues, props, setStates, setState, reset };
}

export interface UseTableProps<
  DataSource extends Record<string, any> = Record<string, any>,
  U extends ParamsType = ParamsType,
  ValueType = 'text',
> extends Omit<MyTableProps<DataSource, U, ValueType>, 'search' | 'pagination'> {
  search?: UseSearchProps;
  pagination?: UsePaginationProps;
  ready?: boolean;
  immediate?: boolean;
  watch?: boolean | string[] | ((params: MyRecord, prevParams: MyRecord) => boolean);
}

export type UseTableResult<
  DataSource extends Record<string, any> = Record<string, any>,
  U extends ParamsType = ParamsType,
  ValueType = 'text',
> = ReturnType<typeof useTable<DataSource, U, ValueType>>;

export function useTable<
  DataSource extends Record<string, any> = Record<string, any>,
  U extends ParamsType = ParamsType,
  ValueType = 'text',
>(props?: UseTableProps<DataSource, U, ValueType>) {
  const {
    search,
    pagination,
    request,
    params,
    ready,
    immediate,
    watch,
    rowSelection,
    rowKey,
    loading: userLoading,
    dataSource: userDataSource,
    ...props_
  } = useMemo(
    () => ({
      ready: true,
      immediate: true,
      watch: false,
      rowKey: 'id',
      ...props,
    }),
    [props]
  );
  const [states, setStates] = useState<MyTableProps<DataSource, U, ValueType>>({
    loading: false,
    dataSource: [],
  });
  const [requestTrigger, setRequestTrigger] = useState<[]>();
  const searchResult = useSearch(search);
  const paginationResult = usePagination(pagination);
  const [selectedRows, setSelectedRows] = useState<DataSource[]>([]);
  const [onChangeArgs, setOnChangeArgs] =
    useState<
      Parameters<Exclude<MyTableProps<DataSource, U, ValueType>['onChange'], undefined>>
    >();
  const requestTrigger_ = useDeferredValue(requestTrigger);

  const filtersAndSorts = useMemo(() => {
    const filters: MyRecord<FilterValue | null> = {};
    const sorts: MyRecord<SortOrder> = {};

    if (onChangeArgs) {
      const [, filtersValue, sortsValue] = onChangeArgs;
      const sortsValue_ = [sortsValue].flat();

      for (const k in filtersValue) {
        const item = filtersValue[k];
        if (item) {
          filters[k] = item;
        }
      }

      for (const item of sortsValue_) {
        if (item.columnKey) {
          if (item.order) {
            sorts[item.columnKey as string] = item.order;
          }
        }
      }
    }

    return { filters, sorts };
  }, [onChangeArgs]);

  const getKey = useCallback(
    (row: DataSource, index: number) => {
      switch (typeof rowKey) {
        case 'string':
          return rowKey;
        case 'function':
          return rowKey(row, index);
      }
    },
    [rowKey]
  );

  const getKeyValue = useCallback(
    (row: DataSource, index: number) => {
      const key = getKey(row, index);
      return row[key as any];
    },
    [getKey]
  );

  const rowSelection_ = useMemo<typeof rowSelection>(
    () =>
      rowSelection && {
        ...rowSelection,
        selectedRowKeys: selectedRows.map((row, i) => getKeyValue(row, i)),
        onChange: (selectedRowKeys, selectedRows, ...args) => {
          rowSelection?.onChange?.(selectedRowKeys, selectedRows, ...args);
          setSelectedRows(selectedRows);
        },
      },
    [getKeyValue, rowSelection, selectedRows]
  );

  const paginationProps_ = useMemo(
    () => request && paginationResult.props,
    [paginationResult.props, request]
  );

  const hasSelected = useMemo(() => selectedRows.length > 0, [selectedRows]);

  const params_ = useMemo(() => {
    const { sorts } = filtersAndSorts;

    const [key] = Object.keys(sorts);

    let sort: string | undefined;

    if (key) {
      sort = (sorts[key] === 'ascend' ? '+' : '-') + key;
    }

    return Object.assign(
      { sort },
      searchResult.values,
      paginationResult.values,
      params
    );
  }, [searchResult.values, paginationResult.values, params, filtersAndSorts]);

  const setState = useCallback((key: string, value: any) => {
    setStates((prev) => ({ ...prev, [key]: value }));
  }, []);

  const tableProps = useMemo<MyTableProps<DataSource, U, ValueType>>(
    () => ({
      ...states,
      rowKey,
      paginationProps: paginationProps_,
      rowSelection: rowSelection_,
      onChange(...args) {
        setOnChangeArgs(args);
      },
      ...props_,
    }),
    [states, paginationProps_, props_, rowSelection_, rowKey]
  );

  const load = useCallback(() => {
    if (ready) {
      setRequestTrigger([]);
    }
  }, [ready]);

  const reset = useCallback(
    (resetOption?: { search?: boolean; pagination?: boolean }) => {
      const resetOption_ = { search: true, pagination: true, ...resetOption };
      const { searchResult, paginationResult } = refs.current;
      if (resetOption_.search) {
        searchResult.reset();
      }
      if (resetOption_.pagination) {
        paginationResult.reset();
      }
      load();
    },
    [load]
  );

  const refsValue = {
    searchResult,
    filtersAndSorts,
    paginationResult,
    request,
    params_,
    setState,
    load,
    reset,
    immediate,
    watch,
  };
  const prevRefs = useRef(refsValue);
  const refs = useRef(refsValue);
  prevRefs.current = refs.current;
  refs.current = refsValue;

  useLayoutEffect(() => {
    if (userLoading !== undefined) {
      setState('loading', userLoading);
    }
  }, [userLoading, setState]);

  useLayoutEffect(() => {
    if (userDataSource !== undefined) {
      setState('dataSource', userDataSource);
    }
  }, [userDataSource, setState]);

  useLayoutEffect(() => {
    const { immediate, load } = refs.current;
    if (immediate) {
      load();
    }
  }, [ready]);

  useUpdateEffect(() => {
    const { load } = refs.current;
    load();
  }, [paginationResult.values, filtersAndSorts]);

  useLayoutEffect(() => {
    const { params_: prevParams_ } = prevRefs.current;
    const { watch, reset, params_ } = refs.current;
    let watch_ = false;
    if (watch === true) {
      watch_ = true;
    } else if (Array.isArray(watch)) {
      watch_ = watch.some((key) => params_[key] !== prevParams_[key]);
    } else if (typeof watch === 'function') {
      watch_ = watch(params_, prevParams_);
    }
    if (watch_) {
      reset({
        search: false,
      });
    }
  }, [searchResult.values]);

  useLayoutEffect(() => {
    if (requestTrigger_) {
      const {
        request,
        params_,
        filtersAndSorts: { filters, sorts },
        paginationResult,
        setState,
      } = refs.current;
      setState('loading', true);
      request?.({ ...params_ }, sorts, filters as any)
        .then(({ data, total }) => {
          setState('dataSource', data);
          paginationResult.setState('total', total || data?.length || 0);
        })
        .finally(() => {
          setState('loading', false);
        });
    }
  }, [requestTrigger_]);

  useLayoutEffect(() => {
    setSelectedRows([]);
  }, [states.dataSource]);

  return {
    states,
    search: searchResult,
    searchControlModels: searchResult.controlModels,
    pagination: paginationResult,
    tableProps,
    load,
    reset,
    setState,
    selectedRows,
    hasSelected,
    setSelectedRows,
  };
}
